/**
 * OpenRouter provider routing for Studio GPT text models.
 * 2026-10-29 (Dallas): GPT Luna / Terra / Sol run on OpenAI only, never
 * Azure, never auto-routing, no fallback to another host.
 */
/**
 * Speed (service) tier. 2026-10-29 (Dallas): every model defaults to Fast
 * (OpenRouter endpoint openai/fast, OpenAI priority tier). Not Flex, not
 * Standard, not Ultrafast. Override per deploy with STUDIO_OPENAI_SPEED_TIER
 * = fast | standard | flex | ultrafast. Billing assumes Fast (2x standard list).
 */
export type OpenAISpeedTier = "fast" | "standard" | "flex" | "ultrafast";
export const DEFAULT_OPENAI_SPEED_TIER: OpenAISpeedTier = "fast";

export function openAISpeedTier(): OpenAISpeedTier {
  const raw = String(process.env.STUDIO_OPENAI_SPEED_TIER ?? "").trim().toLowerCase();
  return raw === "standard" || raw === "flex" || raw === "ultrafast" || raw === "fast"
    ? raw
    : DEFAULT_OPENAI_SPEED_TIER;
}

function openAIEndpointSlug(tier: OpenAISpeedTier): string {
  return tier === "standard" ? "openai" : `openai/${tier}`;
}

/** OpenAI only (never Azure, never auto), on the chosen speed tier, no fallbacks. */
export function openRouterOpenAIOnly(tier: OpenAISpeedTier = openAISpeedTier()) {
  const slug = openAIEndpointSlug(tier);
  return { only: [slug], order: [slug], allow_fallbacks: false };
}

/** Default pin (Fast unless the env overrides it). */
export const OPENROUTER_OPENAI_ONLY = openRouterOpenAIOnly(DEFAULT_OPENAI_SPEED_TIER);

/** True for OpenRouter ids served by OpenAI (openai/*, ~openai/*). */
export function isOpenAIRoutedModelId(modelId: unknown): boolean {
  return /^~?openai\//i.test(String(modelId ?? "").trim());
}

/** Body fields to spread into a raw OpenRouter request for this model. */
export function openRouterProviderFields(modelId: unknown): { provider?: ReturnType<typeof openRouterOpenAIOnly> } {
  return isOpenAIRoutedModelId(modelId) ? { provider: openRouterOpenAIOnly() } : {};
}

/**
 * Pins `provider` on every OpenRouter JSON request whose model is an OpenAI id. opts.speed
 * (a chat agent's Fast/Normal mode) picks the OpenAI tier; for other vendors "fast" asks
 * OpenRouter for the highest-throughput provider.
 */
export function openAIProviderPinFetch(
  baseFetch: typeof fetch = fetch,
  opts: { speed?: "fast" | "standard" } = {},
): typeof fetch {
  return (async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (typeof init?.body === "string" && url.includes("openrouter.ai")) {
      try {
        const body = JSON.parse(init.body) as Record<string, unknown>;
        if (body && isOpenAIRoutedModelId(body.model)) {
          body.provider = opts.speed ? openRouterOpenAIOnly(opts.speed) : openRouterOpenAIOnly();
          init = { ...init, body: JSON.stringify(body) };
        } else if (body && opts.speed === "fast" && typeof body.model === "string") {
          body.provider = { ...((body.provider as Record<string, unknown> | undefined) ?? {}), sort: "throughput" };
          init = { ...init, body: JSON.stringify(body) };
        }
      } catch {
        /* not JSON: send as is */
      }
    }
    return baseFetch(input, init);
  }) as typeof fetch;
}

/**
 * Process-wide safety net (installed with the AI usage meter): any OpenRouter JSON request for an
 * openai/* model that does not already carry a provider.only list is pinned to OpenAI only, no
 * fallbacks. Covers raw fetch call sites (voice, vision, Ad Engine, captions, browser tools,
 * simulations, planner) as well as the AI SDK providers. A caller's own pin (e.g. a chat agent's
 * Normal/Fast tier) is kept.
 */
export function openAIPinIfMissingFetch(baseFetch: typeof fetch): typeof fetch {
  return (async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (typeof init?.body === "string" && url.includes("openrouter.ai")) {
      try {
        const body = JSON.parse(init.body) as Record<string, unknown>;
        const provider = body?.provider as { only?: unknown } | undefined;
        if (body && isOpenAIRoutedModelId(body.model) && !(Array.isArray(provider?.only) && provider.only.length)) {
          body.provider = openRouterOpenAIOnly();
          init = { ...init, body: JSON.stringify(body) };
        }
      } catch {
        /* not JSON: send as is */
      }
    }
    return baseFetch(input, init);
  }) as typeof fetch;
}
